<script lang="ts">
	import { marketsConfig } from '$lib/config/markets';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getNumBackInUnits, nFormatter, addDollarSign, toBN } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import { fade } from 'svelte/transition';
	import CounterDigit from './CounterDigit.svelte';
	import { currentMarket } from './store';
	import FundingRateDesktop from './topInfoSection/FundingRateDesktop.svelte';
	import SelectMarket from './topInfoSection/SelectMarket.svelte';

	let marketSize: string = '0';
	let shortOI: string = '0';
	let longOI: string = '0';
	let showMarketSelector = false;

	$: is24HrChangePositive = $currentMarket && new BigNumber($currentMarket?.change24h).gte(0);
	let priceColor = 'text-fx-gradient-5';
	let lastPrice: string = '0';
	$: if ($currentMarket) {
		marketSize = nFormatter(
			getNumBackInUnits(
				new BigNumber($currentMarket.maxMarketValue)
					.times($currentMarket.currentMarketPrice)
					.div(1e18)
					.toString(),
				18,
				2
			),
			2
		);
		shortOI = nFormatter(
			getNumBackInUnits(
				new BigNumber($currentMarket.shortOI)
					.times($currentMarket.currentMarketPrice)
					.div(1e18)
					.toString(),
				18,
				2
			),
			2
		);
		longOI = nFormatter(
			getNumBackInUnits(
				new BigNumber($currentMarket.longOI)
					.times($currentMarket.currentMarketPrice)
					.div(1e18)
					.toString(),
				18,
				2
			),
			2
		);

		const skew = new BigNumber($currentMarket.longOI).minus($currentMarket.shortOI);
		const indexMultiplier = new BigNumber(1).plus(skew.div($currentMarket.skewScale)).toString();

		if (new BigNumber($currentMarket.currentMarketPrice).gt(new BigNumber(lastPrice))) {
			priceColor = 'text-fx-gradient-23';
		} else if (new BigNumber($currentMarket.currentMarketPrice).lt(new BigNumber(lastPrice))) {
			priceColor = 'text-fx-gradient-22';
		}
		lastPrice = $currentMarket.currentMarketPrice;
	}

	const toggleMarketSelector = () => {
		showMarketSelector = !showMarketSelector;
	};

	$: marketPrices = $currentMarket
		? addDollarSign(
				getNumBackInUnits(
					$currentMarket.currentMarketPrice,
					18,
					marketsConfig[$currentMarket.asset].showDecimals
				),
				marketsConfig[$currentMarket.asset].showDecimals
		  )
		: '0.00';

	$: digits = marketPrices.split('');

	$: skewRate =
		$currentMarket && $currentMarket.longOI && $currentMarket.shortOI
			? new BigNumber($currentMarket.longOI)
					.div(new BigNumber($currentMarket.shortOI).plus($currentMarket.longOI))
					.toNumber()
			: 0.5;
</script>

<div
	class="relative flex-col border-fx-grey-500 sm:border sm:border-l-0 whitespace-nowrap not-scrollbar"
>
	{#if $currentMarket}
		<div
			class="sm:bg-fx-gradient-7-50 flex items-center py-2 sm:py-3 px-0 sm:px-5 w-full overflow-auto no-scrollbar"
			data-title="Selected Market!"
			data-intro="You can view all market related information in this section"
			data-step="1"
		>
			<div
				on:click={toggleMarketSelector}
				on:keydown={toggleMarketSelector}
				class="cursor-pointer relative min-w-fit"
				role="presentation"
			>
				<GradientBorder
					gradient="bg-fx-gradient-6"
					paddingClass="p-px"
					borderRadius="rounded-xl squircle-xl"
					outerClassList=""
					innerClassList="flex justify-center items-center bg-fx-grey-900 px-4 sm:px-2.5 py-2 sm:py-2 gap-2"
				>
					<img
						src={getTokenLogo($currentMarket.marketIdentifier)}
						loading="lazy"
						class="w-8 h-8 rounded-full border border-fx-grey-500"
						alt="logo"
					/>

					<div class="text-fx-grey-300 font-medium text-sm">
						{getDisplayName($currentMarket.marketIdentifier)}
					</div>
					<img
						src={'down-arrow.png'}
						loading="lazy"
						alt=""
						class="w-2.5 transition-transform duration-300 ease-in-out"
						class:flip-arrow={showMarketSelector}
					/>
				</GradientBorder>
			</div>

			<div class="flex-col items-start px-5 sm:ml-0 ml-auto">
				<div class="{priceColor} text-2xl font-semibold flex">
					{#each digits as digit, index (index)}
						<div transition:fade|global={{ duration: 100 }}>
							<CounterDigit value={digit} {priceColor} />
						</div>
					{/each}
				</div>
				<div class="flex sm:hidden gap-1 justify-end">
					<div class="text-fx-grey-300 text-xs">24h:</div>
					<div class="text-fx-gradient-5 text-xs font-medium text-right">
						{$currentMarket.change24hPercentage}%
					</div>
				</div>
			</div>
			<div class="hidden sm:flex flex-col items-start pl-3 border-l-fx-grey-600 border-l mx-2">
				<div class="text-fx-grey-400 text-xs">Index Price</div>
				<div class="text-fx-grey-200 text-sm font-semibold">
					{addDollarSign(
						getNumBackInUnits(
							$currentMarket.currentOraclePrice,
							18,
							marketsConfig[$currentMarket.asset].showDecimals
						),
						marketsConfig[$currentMarket.asset].showDecimals
					)}
				</div>
			</div>
			<div class="hidden sm:flex flex-col items-start pl-3 border-l-fx-grey-600 border-l mx-2">
				<div class="text-fx-grey-400 text-xs">24h Change</div>
				<div
					class="text-fx-gradient-22 text-sm font-semibold flex gap-2"
					class:text-fx-gradient-23={is24HrChangePositive}
				>
					<div>
						{addDollarSign(
							$currentMarket.change24h,
							marketsConfig[$currentMarket.asset].showDecimals
						)}
					</div>
					<div>
						{$currentMarket.change24hPercentage}%
					</div>
				</div>
			</div>
			<div class="hidden sm:flex flex-col items-start pl-3 border-l-fx-grey-600 border-l">
				<div class="text-fx-grey-400 text-xs">Open Interest (L)</div>
				<div class="text-fx-grey-200 text-sm font-semibold">
					${longOI}

					<span class="text-fx-grey-400">/</span>

					${marketSize}
				</div>
			</div>
			<div class="hidden sm:flex flex-col items-start pl-3 border-l-fx-grey-600 border-l ml-3">
				<div class="text-fx-grey-400 text-xs">Open Interest (S)</div>
				<div class="text-fx-grey-200 text-sm font-semibold">
					${shortOI}
					<span class="text-fx-grey-400">/</span>

					${marketSize}
				</div>
			</div>

			<div
				data-title="Funding Rate"
				data-intro="This is the current funding rate that SHORTs pay to LONGs"
				data-step="2"
			>
				<FundingRateDesktop />
			</div>
			<div class="hidden sm:flex flex-col items-start pl-3 border-l-fx-grey-600 border-l mx-2">
				<div class="text-fx-grey-400 text-xs">Skew</div>
				<div class="text-fx-grey-200 text-sm font-semibold">
					<span class="text-fx-green">
						{isNaN(skewRate) ? 0 : (skewRate * 100).toFixed(2)}%
					</span>
					<span class="text-fx-grey-400">/</span>
					<span class="text-fx-red">
						{isNaN(skewRate) ? 0 : ((1 - skewRate) * 100).toFixed(2)}%
					</span>
				</div>
			</div>
		</div>
	{:else}
		<div
			class="bg-fx-gradient-8-50 grid grid-cols-2 sm:grid-cols-10 items-center py-2 sm:py-3 px-2 sm:px-5 w-full rounded-lg squircle-lg gap-2"
		>
			<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
			<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-1 rounded-lg squircle-lg" />
			<div
				class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-1 rounded-lg squircle-lg hidden sm:block"
			/>
			<div
				class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-1 rounded-lg squircle-lg hidden sm:block"
			/>
			<div
				class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-1 rounded-lg squircle-lg hidden sm:block"
			/>
			<div
				class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-1 rounded-lg squircle-lg hidden sm:block"
			/>
			<div />
			<div />
		</div>
	{/if}
	<SelectMarket bind:showMarketSelector />
</div>

<style>
	.flip-arrow {
		transform: rotate(180deg);
	}
</style>
